'use client';

import { styled } from '@linaria/react';
import RankSymbol from '@fe-news/components/atoms/Member/RankSymbol';
import HotRate from '@fe-news/components/atoms/Member/HotRate';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { useQuery } from '@tanstack/react-query';
import { fetchNewsById } from '@fe-news/services/news';
import CollectionSaveButton from '@fe-news/components/atoms/CollectionSaveButton';
import HotCollectionCardSkeleton from './HotCollectionCardSkeleton';
import Link from 'next/link';
import { GTMEventKey, sendGTM, CustomEventName } from '@fe-cnyes/fe-common-gtm';
import { postTimeDisplayFactory } from '@fe-common-utils/libs/time';

// type

import { HotCollectionCardProps } from '@fe-news/constants/my-track/type';

const Container = styled.div`
  display: flex;
  column-gap: 12px;
  padding-bottom: 13px;
  border-bottom: 1px solid #e2e8f1;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 12px;
  flex: 1;
`;

const SymbolWrapper = styled.div`
  align-self: center;

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    padding-top: 31px;
    padding-bottom: 12px;
  }
`;

const SubTitle = styled.div`
  display: flex;
  align-items: center;
  font-size: 13px;
  column-gap: 8px;
  letter-spacing: 0.5px;
  font-weight: 400;

  .time {
    color: #848d97;
  }

  .dot {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background-color: #e2e8f1;
  }

  .category {
    color: #515c69;
  }

  .divide {
    width: 2px;
    height: 16px;
    padding: 1px 0px;
    background-color: #e2e8f1;
  }
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.5px;
  color: #383838;

  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: ${SCREEN_SIZE.DESKTOP}px) {
    -webkit-line-clamp: 2;
  }
`;

const TitleAndSaveButtonContainer = styled.div`
  display: flex;
  column-gap: 12px;
  align-items: center;
`;

const HotCollectionCard = ({ id, hotRate, index, isRankingList }: HotCollectionCardProps) => {
  const { data: newsData, isLoading } = useQuery({
    queryKey: ['news', `id: ${id}`],
    queryFn: () => fetchNewsById(id)
  });

  const publishTime = newsData ? newsData.publishAt * 1000 : Date.now();
  const formatTime = postTimeDisplayFactory(publishTime).display ?? '--';

  const breadcrumb = newsData?.breadcrumbs?.[0]?.name ?? '';

  const title = newsData?.title ?? '';

  const onClickTitle = () => {
    sendGTM({
      [GTMEventKey.EVENT_NAME]: CustomEventName.CLICK_NEWS,
      [GTMEventKey.SECTION]: isRankingList ? 'News_Category_熱門排行' : 'News_Category_我的收藏',
      [GTMEventKey.CLICK_ITEM]: `熱門收藏＿${id}_${title}`
    });
  };

  const collectionGtmEvent = {
    [GTMEventKey.EVENT_NAME]: CustomEventName.FAVORITE,
    [GTMEventKey.SECTION]: isRankingList ? 'News_Category_熱門排行' : 'News_Category_我的收藏',
    [GTMEventKey.CLICK_ITEM]: `熱門收藏＿${id}_${title}`
  };

  if (isLoading) {
    return <HotCollectionCardSkeleton />;
  }

  return (
    <Container>
      <SymbolWrapper>
        <RankSymbol index={index} isShowIndex />
      </SymbolWrapper>
      <ContentContainer>
        <SubTitle>
          <p className='time'>{formatTime}</p>
          <div className='dot' />
          <p className='category'>{breadcrumb}</p>
          <div className='divide' />
          <HotRate hotRank={hotRate} />
        </SubTitle>
        <TitleAndSaveButtonContainer>
          <Link href={`/news/id/${id}`} onClick={onClickTitle}>
            <Title>{title}</Title>
          </Link>
          <CollectionSaveButton newsId={id} gtmEvent={collectionGtmEvent} isRankingList={true} />
        </TitleAndSaveButtonContainer>
      </ContentContainer>
    </Container>
  );
};

export default HotCollectionCard;
